import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Board, useDuplicateBoard } from '@/entities/board';
import { useWorkspaces } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, TextField } from '@/shared/ui';

import styles from './Portability.module.css';

const NAME_MAX = 100;
const CREATE_BOARD = 'workspace.create_board';

interface Props {
  source: Pick<Board, 'id' | 'name' | 'workspace_id'>;
  onDuplicated: (board: Board) => void;
  onClose: () => void;
}

/**
 * "Duplicate board": a name, the destination workspace (only where the viewer may create boards)
 * and whether the cards come along. The copy is a new independent board owned by the viewer.
 */
export function DuplicateBoardDialog({ source, onDuplicated, onClose }: Props) {
  const { t } = useTranslation();
  const duplicate = useDuplicateBoard(source.id);
  const workspaces = useWorkspaces();
  const targets = (workspaces.data ?? []).filter((w) => w.permissions.includes(CREATE_BOARD));
  const [name, setName] = useState(() =>
    t('portability.duplicate.defaultName', { name: source.name }).slice(0, NAME_MAX),
  );
  const [target, setTarget] = useState(source.workspace_id ?? '');
  const [cards, setCards] = useState(true);
  // One id per opened dialog: a retry or a double click returns the same copy.
  const [requestId] = useState(() => crypto.randomUUID());
  const [touched, setTouched] = useState(false);
  const sending = useRef(false);
  const nameError = touched && name.trim() === '' ? t('validation.REQUIRED') : undefined;

  const submit = () => {
    setTouched(true);
    if (name.trim() === '' || sending.current) return;
    sending.current = true;
    duplicate.mutate(
      {
        requestId,
        name: name.trim(),
        includeCards: cards,
        ...(target !== source.workspace_id && { workspaceId: target }),
      },
      {
        onSuccess: ({ board }) => {
          onDuplicated(board);
        },
        onError: () => {
          sending.current = false;
        },
      },
    );
  };

  return (
    <Dialog title={t('portability.duplicate.title', { name: source.name })} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t('portability.duplicate.name')}
          value={name}
          maxLength={NAME_MAX}
          autoFocus
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        {targets.length > 1 && (
          <Combobox
            label={t('portability.duplicate.workspace')}
            value={target}
            options={targets.map((w) => ({
              value: w.id,
              label: w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
            }))}
            onChange={setTarget}
          />
        )}
        <label className={styles.format}>
          <input
            type="checkbox"
            checked={cards}
            onChange={(event) => {
              setCards(event.target.checked);
            }}
          />
          <span>
            <span className={styles.formatTitle}>{t('portability.duplicate.withCards')}</span>
            <span className={styles.formatDesc}>{t('portability.duplicate.withCardsHint')}</span>
          </span>
        </label>
        <p className={styles.note}>{t('portability.duplicate.note')}</p>
        <FormError error={duplicate.error} />
        {duplicate.isPending && (
          <p className={styles.note} role="status">
            {t('portability.duplicate.working')}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={duplicate.isPending}>
            {t('portability.duplicate.action')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
